import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Spinner,
  Center,
  Divider,
  Badge,
  Tag,
  TagLabel,
  Button,
  useColorModeValue,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Avatar,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Switch,
  Tooltip,
} from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FiSearch,
  FiClock,
  FiFilter,
  FiX,
  FiUser,
  FiLogOut,
  FiHelpCircle,
  FiSidebar,
} from 'react-icons/fi'
import { SearchParams, SearchFileSuggestion } from '@/types'
import SearchFilterSidebar from '@/components/Search/SearchFilterSidebar'
import SearchResultCard from '@/components/Search/SearchResultCard'
import SearchSuggestionsDropdown, {
  SuggestionItem,
} from '@/components/Search/SearchSuggestionsDropdown'
import apiService from '@/services/api'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { MotionBox } from '@/components/Common/MotionBox'
import { useAnalytics } from '@/hooks/useAnalyticsWithUser'
import { pageVariants, containerVariants, listItemVariants } from '@/theme/animations'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  performSearch,
  loadMoreSearchResults,
  clearSearchResults,
  selectSearchResults,
  selectSearchLoading,
  selectSearchError,
  selectSearchLoadingMore,
  selectSearchLoadMoreError,
  selectLastSearchParams,
  selectIsCurrentSearch,
} from '@/store/slices/searchSlice'
import { selectFolderTree } from '@/store/selectors'
import { findFolderById } from '@/utils/folderUtils'
import { useAuth } from '@/contexts/AuthContext'
import LanguageSwitcher from '@/components/Common/LanguageSwitcher'

const MotionVStack = motion(VStack as any)

// Results scroll position per search URL, so Back from a preview lands where the user was
const resultsScrollPositions = new Map<string, number>()

// View preferences kept per browser; storage can be blocked, so default to off
const FILTERS_COLLAPSED_KEY = 'search-filters-collapsed'
const SHOW_SUMMARIES_KEY = 'search-show-summaries'

const readFlag = (key: string) => {
  try {
    return localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

const writeFlag = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, String(value))
  } catch {
    // Not remembered, still applied
  }
}

export default function SearchContent() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const searchResults = useAppSelector(selectSearchResults)
  const isLoading = useAppSelector(selectSearchLoading)
  const searchError = useAppSelector(selectSearchError)
  const lastSearchParams = useAppSelector(selectLastSearchParams)
  const isLoadingMore = useAppSelector(selectSearchLoadingMore)
  const loadMoreError = useAppSelector(selectSearchLoadMoreError)
  const folderTree = useAppSelector(selectFolderTree)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(() =>
    readFlag(FILTERS_COLLAPSED_KEY)
  )
  const [showSummaries, setShowSummaries] = useState(() => readFlag(SHOW_SUMMARIES_KEY))
  const toast = useToast()
  const { trackEvent } = useAnalytics()
  const { t } = useAppTranslation('search')
  const { user, logout } = useAuth()

  const [rightSearchQuery, setRightSearchQuery] = useState(
    () => searchParams.get('q') || searchParams.get('query') || ''
  )

  useEffect(() => {
    setRightSearchQuery(searchParams.get('q') || searchParams.get('query') || '')
  }, [searchParams])

  // Autocomplete suggestions state
  const [suggestedFiles, setSuggestedFiles] = useState<SearchFileSuggestion[]>([])
  const [suggestedTags, setSuggestedTags] = useState<string[]>([])
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false)
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1)
  const suggestionsAbortRef = useRef<AbortController | null>(null)
  const debouncedQuery = useDebouncedValue(rightSearchQuery, 250)

  const submittedQuery = searchParams.get('q') || searchParams.get('query') || ''

  useEffect(() => {
    const query = debouncedQuery.trim()

    if (!isSearchFocused || query.length < 2 || query === submittedQuery.trim()) {
      suggestionsAbortRef.current?.abort()
      setSuggestedFiles([])
      setSuggestedTags([])
      setIsSuggestionsOpen(false)
      setActiveSuggestionIndex(-1)
      return
    }

    suggestionsAbortRef.current?.abort()
    const controller = new AbortController()
    suggestionsAbortRef.current = controller

    apiService
      .getSearchSuggestions(query, 8, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return
        setSuggestedFiles(response.suggestions.files)
        setSuggestedTags(response.suggestions.tags)
        setIsSuggestionsOpen(true)
        setActiveSuggestionIndex(-1)
      })
      .catch(() => {
        // Autocomplete failures are silent — the user can still submit the search
      })

    return () => controller.abort()
  }, [debouncedQuery, isSearchFocused, submittedQuery])

  const suggestionItems = useMemo<SuggestionItem[]>(() => {
    const query = debouncedQuery.trim()
    if (!query) return []
    return [
      { type: 'query', value: query },
      ...suggestedFiles.map((file) => ({ type: 'file' as const, value: file })),
      ...suggestedTags.map((tag) => ({ type: 'tag' as const, value: tag })),
    ]
  }, [debouncedQuery, suggestedFiles, suggestedTags])

  const closeSuggestions = () => {
    setIsSuggestionsOpen(false)
    setActiveSuggestionIndex(-1)
  }

  const sidebarBg = useColorModeValue('white', 'gray.800')
  const sidebarBorderColor = useColorModeValue('gray.200', 'gray.700')
  // The whole rail takes the filters sidebar's header color, so it reads as that panel folded
  const railBg = useColorModeValue('gray.50', 'gray.750')
  const railIconColor = useColorModeValue('gray.600', 'gray.400')

  // Parse search parameters from URL
  const parseSearchParams = (): SearchParams | null => {
    const params: SearchParams = { query: '' }

    // Handle the main query parameter (could be 'q' or 'query')
    const query = searchParams.get('q') || searchParams.get('query') || ''
    if (query) {
      params.query = query
    }

    // Get all other parameters from URL
    for (const [key, value] of searchParams.entries()) {
      if (key === 'q' || key === 'query' || key === 'offset') {
        // Query handled above; the offset belongs to infinite scroll, not the URL
        continue
      } else if (key === 'tags') {
        // Handle multiple tag values
        if (!params.tags) params.tags = []
        params.tags.push(value)
      } else if (
        key === 'folder_id' ||
        key === 'limit' ||
        key === 'file_size_min_mb' ||
        key === 'file_size_max_mb'
      ) {
        // Handle numeric parameters
        const numValue = parseFloat(value) // Use parseFloat for decimal support
        if (!isNaN(numValue)) {
          ;(params as Record<string, any>)[key] = numValue
        }
      } else {
        // Handle string parameters
        ;(params as Record<string, any>)[key] = value
      }
    }

    // Set default limit if not specified
    if (params.query && !params.limit) {
      params.limit = 50
    }

    return params.query ? params : null
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const parsedSearchParams = useMemo(() => parseSearchParams(), [searchParams])

  // On mount the store may already hold this search (Back from a preview): show it
  // as it was, without replaying the entrance animation, and restore the scroll
  const isCurrentSearch = useAppSelector(
    (state) => !!parsedSearchParams && selectIsCurrentSearch(state, parsedSearchParams)
  )
  const showResultsInstantlyRef = useRef(isCurrentSearch)
  const resultsScrollRef = useRef<HTMLDivElement>(null)
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const savedScrollTop = resultsScrollPositions.get(location.search)
    if (showResultsInstantlyRef.current && savedScrollTop && resultsScrollRef.current) {
      resultsScrollRef.current.scrollTop = savedScrollTop
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isLoading) showResultsInstantlyRef.current = false
  }, [isLoading])

  const handleResultsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    resultsScrollPositions.set(location.search, event.currentTarget.scrollTop)
  }

  const hasMoreResults = searchResults?.next_offset != null
  const loadedResultsCount = searchResults?.results.length ?? 0
  const pageSize = lastSearchParams?.limit ?? 50

  const handleLoadMore = () => {
    dispatch(loadMoreSearchResults())
  }

  // Infinite scroll: load the next page once the end of the list is near. Rebuilt
  // after every page so a list that still doesn't fill the view keeps loading
  useEffect(() => {
    const root = resultsScrollRef.current
    const sentinel = loadMoreSentinelRef.current
    if (!root || !sentinel || !hasMoreResults || isLoading || isLoadingMore || loadMoreError) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) dispatch(loadMoreSearchResults())
      },
      { root, rootMargin: '0px 0px 600px 0px', threshold: 0 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [dispatch, hasMoreResults, isLoading, isLoadingMore, loadMoreError, loadedResultsCount])

  // Load search results when component mounts or URL changes
  useEffect(() => {
    const params = parseSearchParams()
    if (params) {
      dispatch(performSearch(params))
    } else {
      dispatch(clearSearchResults())
    }
  }, [searchParams, dispatch])

  // Handle search errors with toast notifications
  useEffect(() => {
    if (searchError) {
      toast({
        title: t('errors.searchFailed'),
        description: searchError,
        status: 'error',
        duration: 4000,
      })
    }
  }, [searchError, toast, t])

  // Track analytics once per search, not again for every page scrolled in. Whatever
  // the store held when the page mounted was already tracked
  const trackedSearchRef = useRef(lastSearchParams)
  useEffect(() => {
    if (
      searchResults &&
      lastSearchParams &&
      !isLoading &&
      !searchError &&
      trackedSearchRef.current !== lastSearchParams
    ) {
      trackedSearchRef.current = lastSearchParams
      const hasResults = searchResults.results && searchResults.results.length > 0

      trackEvent('search_performed', {
        query_length: lastSearchParams.query.length,
        has_filters: Object.keys(lastSearchParams).length > 1,
        results_count: searchResults.results?.length || 0,
        total_results: searchResults.total_results || 0,
        search_time_ms: searchResults.search_time_ms || 0,
        search_type: searchResults.search_type || lastSearchParams.search_type || 'hybrid',
        has_results: hasResults,
        query: lastSearchParams.query.toLowerCase(),
      })

      // Track zero results separately for analysis
      if (!hasResults) {
        trackEvent('search_no_results', {
          query: lastSearchParams.query.toLowerCase(),
          query_length: lastSearchParams.query.length,
          search_type: searchResults.search_type || lastSearchParams.search_type || 'hybrid',
        })
      }
    }
  }, [searchResults, lastSearchParams, isLoading, searchError, trackEvent])

  const handleAdvancedSearch = (newSearchParams: SearchParams) => {
    // Track advanced search usage
    trackEvent('search_advanced_used', {
      has_date_filter: !!(newSearchParams.date_from || newSearchParams.date_to),
      has_type_filter: !!newSearchParams.mime_type,
      has_folder_filter: !!newSearchParams.folder_id,
      has_tags_filter: !!(newSearchParams.tags && newSearchParams.tags.length > 0),
      has_file_size_filter: !!(
        newSearchParams.file_size_min_mb || newSearchParams.file_size_max_mb
      ),
      search_type: newSearchParams.search_type || 'hybrid',
      sort_by: newSearchParams.sort_by || 'relevance',
      filters_count: Object.keys(newSearchParams).filter(
        (key) =>
          key !== 'query' &&
          newSearchParams[key as keyof SearchParams] !== undefined &&
          newSearchParams[key as keyof SearchParams] !== null &&
          newSearchParams[key as keyof SearchParams] !== ''
      ).length,
    })

    // Build URL with all search parameters
    const params = new URLSearchParams()

    Object.entries(newSearchParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((item) => params.append(key, item.toString()))
        } else {
          params.append(key, value.toString())
        }
      }
    })

    setSearchParams(params)
    setIsMobileSidebarOpen(false)
  }

  const handleRightSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    closeSuggestions()
    handleAdvancedSearch({ ...(parsedSearchParams || {}), query: rightSearchQuery })
  }

  const handleSuggestionSelect = (item: SuggestionItem) => {
    closeSuggestions()

    if (item.type === 'file') {
      trackEvent('search_suggestion_selected', { type: 'file' })
      navigate(`/preview/${item.value.id}?from=search&q=${encodeURIComponent(rightSearchQuery)}`, {
        state: { searchUrl: location.pathname + location.search },
      })
      return
    }

    const query = item.value
    trackEvent('search_suggestion_selected', { type: item.type })
    setRightSearchQuery(query)
    handleAdvancedSearch({ ...(parsedSearchParams || {}), query })
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isSuggestionsOpen || suggestionItems.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveSuggestionIndex((prev) => (prev + 1) % suggestionItems.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveSuggestionIndex((prev) => (prev <= 0 ? suggestionItems.length - 1 : prev - 1))
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault()
      handleSuggestionSelect(suggestionItems[activeSuggestionIndex])
    } else if (e.key === 'Escape') {
      closeSuggestions()
    }
  }

  const clearAllFilters = () => {
    if (lastSearchParams?.query) {
      setSearchParams({ query: lastSearchParams.query })
    } else {
      setSearchParams({})
    }
  }

  const setFiltersCollapsed = (collapsed: boolean) => {
    setIsFiltersCollapsed(collapsed)
    writeFlag(FILTERS_COLLAPSED_KEY, collapsed)
    if (collapsed) setIsMobileSidebarOpen(false)
  }

  const handleShowSummariesChange = (show: boolean) => {
    setShowSummaries(show)
    writeFlag(SHOW_SUMMARIES_KEY, show)
    trackEvent('search_summaries_toggled', { show_summaries: show })
  }

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  // Check if there are active filters beyond just the query
  const hasActiveFilters =
    lastSearchParams &&
    Object.keys(lastSearchParams).some(
      (key) =>
        key !== 'query' &&
        key !== 'search_type' &&
        key !== 'limit' &&
        key !== 'sort_by' &&
        key !== 'sort_direction' &&
        lastSearchParams[key as keyof SearchParams] !== undefined
    )

  return (
    <MotionBox h="full" initial="initial" animate="animate" variants={pageVariants}>
      <Flex h="full" overflow="hidden">
        {/* Filter Sidebar */}
        <Box
          w="320px"
          minW="320px"
          borderRight="1px solid"
          borderColor={sidebarBorderColor}
          display={{
            base: isMobileSidebarOpen ? 'flex' : 'none',
            lg: isFiltersCollapsed ? 'none' : 'flex',
          }}
          flexDirection="column"
          bg={sidebarBg}
          flexShrink={0}
        >
          <SearchFilterSidebar
            key={searchParams.toString()}
            onSearch={handleAdvancedSearch}
            initialParams={parsedSearchParams || undefined}
            onCollapse={() => setFiltersCollapsed(true)}
          />
        </Box>

        {/* Collapsed filters: a thin rail with the same toggle, desktop only */}
        <Box
          w="49px"
          borderRight="1px solid"
          borderColor={sidebarBorderColor}
          display={{ base: 'none', lg: isFiltersCollapsed ? 'flex' : 'none' }}
          flexDirection="column"
          bg={railBg}
          flexShrink={0}
        >
          <Center py={4}>
            <Tooltip label={t('results.showFilters')} placement="right">
              <IconButton
                aria-label={t('results.showFilters')}
                icon={<FiSidebar />}
                variant="ghost"
                size="sm"
                color={railIconColor}
                onClick={() => setFiltersCollapsed(false)}
              />
            </Tooltip>
          </Center>
        </Box>

        {/* Right Panel */}
        <Box flex={1} display="flex" flexDirection="column" overflow="hidden" bg="gray.50">
          {/* Top bar: language + profile */}
          <HStack justify="flex-end" px={4} py={2} flexShrink={0} spacing={2}>
            <Button
              display={{ base: 'flex', lg: 'none' }}
              leftIcon={<FiFilter />}
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileSidebarOpen((v) => !v)}
            >
              {t('results.advancedSearch')}
            </Button>
            <LanguageSwitcher variant="compact" size="sm" />
            <Menu>
              <MenuButton>
                <Box
                  cursor="pointer"
                  py={1}
                  px={2}
                  borderRadius="lg"
                  _hover={{ bg: 'gray.200' }}
                  transition="colors 0.15s"
                >
                  <Avatar size="sm" name={user?.name} boxShadow="sm" />
                </Box>
              </MenuButton>
              <MenuList
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="xl"
                py={2}
              >
                <MenuItem
                  icon={<FiUser />}
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => navigate('/account')}
                >
                  {t('navigation.profile', { ns: 'common' })}
                </MenuItem>
                <MenuItem
                  icon={<FiHelpCircle />}
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => navigate('/help')}
                >
                  {t('navigation.help', { ns: 'common' })}
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  icon={<FiLogOut />}
                  onClick={handleLogout}
                  _hover={{ bg: 'red.50', color: 'red.600' }}
                  color="gray.700"
                >
                  {t('logout.button', { ns: 'auth' })}
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>

          {/* Search input */}
          <Box px={8} pb={4} flexShrink={0}>
            <Box as="form" onSubmit={handleRightSearchSubmit} position="relative">
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none" h="12">
                  <FiSearch color="gray.400" size="18px" />
                </InputLeftElement>
                <Input
                  value={rightSearchQuery}
                  onChange={(e) => setRightSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => {
                    setIsSearchFocused(false)
                    closeSuggestions()
                  }}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={t('placeholder', { ns: 'search' })}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="xl"
                  height="12"
                  boxShadow="sm"
                  fontSize="md"
                  _focus={{
                    borderColor: 'brand.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                    bg: 'white',
                  }}
                  _placeholder={{ color: 'gray.500' }}
                />
              </InputGroup>
              {isSuggestionsOpen && suggestionItems.length > 0 && (
                <SearchSuggestionsDropdown
                  query={debouncedQuery.trim()}
                  items={suggestionItems}
                  activeIndex={activeSuggestionIndex}
                  onSelect={handleSuggestionSelect}
                  onHover={setActiveSuggestionIndex}
                />
              )}
            </Box>
          </Box>

          {/* Results — scrollable */}
          <Box flex={1} overflow="auto">
            <MotionVStack
              spacing={0}
              h="full"
              initial="initial"
              animate="animate"
              variants={containerVariants}
            >
              {/* Results Section */}
              <MotionBox
                ref={resultsScrollRef}
                onScroll={handleResultsScroll}
                w="full"
                flex="1"
                overflow="auto"
                variants={listItemVariants}
              >
                <Box maxW="4xl" mx="auto" p={8}>
                  {isLoading ? (
                    <MotionBox
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Center py={20}>
                        <VStack spacing={6}>
                          <Spinner size="xl" color="blue.500" thickness="4px" />
                          <Text color="gray.600" fontSize="lg" fontWeight="medium">
                            {t('results.loading')}
                          </Text>
                        </VStack>
                      </Center>
                    </MotionBox>
                  ) : searchResults ? (
                    <MotionVStack
                      spacing={8}
                      align="stretch"
                      initial={showResultsInstantlyRef.current ? false : 'initial'}
                      animate="animate"
                      variants={containerVariants}
                    >
                      {/* Search Info */}
                      <MotionBox variants={listItemVariants}>
                        <VStack spacing={4} align="stretch">
                          <HStack justify="space-between" align="center">
                            <Text fontSize="xl" fontWeight="bold" color="gray.800">
                              {t('results.found', {
                                count: searchResults.total_results,
                                query: searchResults.search_criteria.query,
                              })}
                            </Text>
                            <HStack spacing={2} fontSize="sm" color="gray.600">
                              <FiClock />
                              <Text fontWeight="medium">
                                {t('results.completedIn', {
                                  time: searchResults.search_time_ms.toFixed(0),
                                })}
                              </Text>
                            </HStack>
                          </HStack>

                          {/* Search Controls */}
                          <HStack justify="space-between" align="center">
                            <Switch
                              size="sm"
                              colorScheme="brand"
                              isChecked={showSummaries}
                              onChange={(e) => handleShowSummariesChange(e.target.checked)}
                              fontSize="sm"
                              color="gray.700"
                            >
                              {t('results.showSummaries')}
                            </Switch>

                            <HStack spacing={2}>
                              {hasActiveFilters && (
                                <Button
                                  leftIcon={<FiX />}
                                  variant="ghost"
                                  size="sm"
                                  colorScheme="red"
                                  onClick={clearAllFilters}
                                >
                                  {t('results.clearFilters')}
                                </Button>
                              )}
                              {hasActiveFilters && (
                                <Badge colorScheme="brand" variant="subtle">
                                  {t('results.filteredResults')}
                                </Badge>
                              )}
                            </HStack>
                          </HStack>

                          {/* Active Filters Display */}
                          {lastSearchParams && (
                            <Box>
                              <HStack spacing={2} wrap="wrap">
                                {lastSearchParams.search_type &&
                                  lastSearchParams.search_type !== 'hybrid' && (
                                    <Tag size="sm" variant="outline">
                                      <TagLabel>
                                        {t('filters.search', { type: lastSearchParams.search_type })}
                                      </TagLabel>
                                    </Tag>
                                  )}
                                {lastSearchParams.folder_id && (
                                  <Tag size="sm" variant="outline">
                                    <TagLabel>
                                      {t('filters.folder', {
                                        name: findFolderById(folderTree, lastSearchParams.folder_id)?.name
                                          ?? lastSearchParams.folder_id,
                                      })}
                                    </TagLabel>
                                  </Tag>
                                )}
                                {lastSearchParams.include_subfolders && (
                                  <Tag size="sm" variant="outline">
                                    <TagLabel>{t('filters.includeSubfolders')}</TagLabel>
                                  </Tag>
                                )}
                                {lastSearchParams.mime_type && (
                                  <Tag size="sm" variant="outline">
                                    <TagLabel>
                                      {t('filters.type', { type: lastSearchParams.mime_type })}
                                    </TagLabel>
                                  </Tag>
                                )}
                                {lastSearchParams.file_name && (
                                  <Tag size="sm" variant="outline">
                                    <TagLabel>
                                      {t('filters.filename', { name: lastSearchParams.file_name })}
                                    </TagLabel>
                                  </Tag>
                                )}
                                {lastSearchParams.date_from && (
                                  <Tag size="sm" variant="outline">
                                    <TagLabel>
                                      {t('filters.from', { date: lastSearchParams.date_from })}
                                    </TagLabel>
                                  </Tag>
                                )}
                                {lastSearchParams.date_to && (
                                  <Tag size="sm" variant="outline">
                                    <TagLabel>
                                      {t('filters.to', { date: lastSearchParams.date_to })}
                                    </TagLabel>
                                  </Tag>
                                )}
                                {lastSearchParams.file_size_min_mb && (
                                  <Tag size="sm" variant="outline">
                                    <TagLabel>
                                      {t('filters.minSize', {
                                        size: lastSearchParams.file_size_min_mb,
                                      })}
                                    </TagLabel>
                                  </Tag>
                                )}
                                {lastSearchParams.file_size_max_mb && (
                                  <Tag size="sm" variant="outline">
                                    <TagLabel>
                                      {t('filters.maxSize', {
                                        size: lastSearchParams.file_size_max_mb,
                                      })}
                                    </TagLabel>
                                  </Tag>
                                )}
                                {lastSearchParams.tags &&
                                  lastSearchParams.tags.map((tag) => (
                                    <Tag key={tag} size="sm" colorScheme="brand" variant="solid">
                                      <TagLabel>{tag}</TagLabel>
                                    </Tag>
                                  ))}
                              </HStack>
                            </Box>
                          )}
                        </VStack>
                      </MotionBox>

                      <Divider borderColor="gray.300" />

                      {/* Results List */}
                      {searchResults.results.length > 0 ? (
                        <>
                          <MotionVStack spacing={6} align="stretch" variants={containerVariants}>
                            <AnimatePresence initial={!showResultsInstantlyRef.current}>
                              {searchResults.results.map((result, index) => (
                                <MotionBox
                                  key={`${result.file_id}-${result.chunk_index}`}
                                  variants={listItemVariants}
                                  custom={index}
                                >
                                  <SearchResultCard
                                    result={result}
                                    searchTerm={searchResults.search_criteria.query}
                                    showSummary={showSummaries}
                                  />
                                </MotionBox>
                              ))}
                            </AnimatePresence>
                          </MotionVStack>

                          {/* Infinite scroll: sentinel + status */}
                          {hasMoreResults && <Box ref={loadMoreSentinelRef} h="1px" />}
                          <Center minH={10}>
                            {isLoadingMore ? (
                              <HStack spacing={3}>
                                <Spinner size="sm" color="blue.500" />
                                <Text fontSize="sm" color="gray.600">
                                  {t('results.loadingMore')}
                                </Text>
                              </HStack>
                            ) : loadMoreError ? (
                              <VStack spacing={2}>
                                <Text fontSize="sm" color="red.500">
                                  {t('results.loadMoreError')}
                                </Text>
                                <Button size="sm" variant="outline" onClick={handleLoadMore}>
                                  {t('buttons.retry', { ns: 'common' })}
                                </Button>
                              </VStack>
                            ) : hasMoreResults ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={handleLoadMore}
                              >
                                {t('buttons.loadMore', { ns: 'common' })}
                              </Button>
                            ) : loadedResultsCount > pageSize ? (
                              <Text fontSize="sm" color="gray.500">
                                {t('results.endOfResults')}
                              </Text>
                            ) : null}
                          </Center>
                        </>
                      ) : (
                        <MotionBox
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4 }}
                        >
                          <Center py={20}>
                            <VStack spacing={6}>
                              <Box fontSize="4xl" color="gray.400">
                                <FiSearch />
                              </Box>
                              <VStack spacing={3}>
                                <Text fontSize="xl" fontWeight="semibold" color="gray.600">
                                  {t('results.noDocuments')}
                                </Text>
                                <Text color="gray.500" textAlign="center" maxW="md">
                                  {t('results.noDocumentsDescription')}
                                </Text>
                              </VStack>
                            </VStack>
                          </Center>
                        </MotionBox>
                      )}
                    </MotionVStack>
                  ) : (
                    <MotionBox
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Center py={20}>
                        <VStack spacing={6}>
                          <Box fontSize="5xl" color="gray.400">
                            <FiSearch />
                          </Box>
                          <VStack spacing={3}>
                            <Text fontSize="xl" fontWeight="semibold" color="gray.600">
                              {t('results.startSearching')}
                            </Text>
                            <Text color="gray.500" textAlign="center" maxW="md">
                              {t('results.startSearchingDescription')}
                            </Text>
                          </VStack>
                        </VStack>
                      </Center>
                    </MotionBox>
                  )}
                </Box>
              </MotionBox>
            </MotionVStack>
          </Box>
        </Box>
      </Flex>
    </MotionBox>
  )
}
